import { DateString } from '../common';
import { ExternalEntityNamedResponseWithExternalId } from './contracts';
import { DirectionApplicationEducationLevel } from './enum';
import { Privilege } from './privileges';
import { SubjectResponse } from './school-tests';

export type DirectionEnum = {
  id: number;
  title: string;
  titleForeign: string;
};

export type Faculty = {
  id: number;
  title: string;
  titleForeign: string;
  externalId: string;
  titleShort: string;
};

export type EducationProgram = {
  id: number;
  code: string;
  title: string;
  titleForeign: string;
};

export type EntranceTestsSet = {
  id: number;
  subjects: SubjectResponse[];
  title: string;
  titleForeign: string;
};

export type TargetOrganization = {
  id: number;
  organizationId: number;
  name: string;
  shortName: string;
  ogrn: string;
  kpp: string;
  inn: string;
  address: string;
  telephone: string;
  email: string;
};

export type Direction = {
  id: number;
  code: string;
  title: string;
  asBenefit: boolean;
  asQuota: boolean;
  titleForeign: string;
  applicationEducationLevel?: DirectionApplicationEducationLevel;
  educationForm: DirectionEnum;
  paymentForm: DirectionEnum;
  budgetLevel?: DirectionEnum;
  educationLevel: DirectionEnum;
  educationProgram?: EducationProgram;
  targetOrganizations?: TargetOrganization[];
  entranceTestsSets: EntranceTestsSet[];
  faculty: ExternalEntityNamedResponseWithExternalId & { titleShort: string };
  isContract: boolean;
  forForeign: boolean;
  educationLanguage: DirectionEnum;
  closed: boolean;
};

export type DirectionEntranceTestPriority = {
  directionId: number;
  subjectId: number;
  priotity: number;
};

export type DirectionFilters = {
  name?: string;
  educationLevelId?: string;
  applicationId?: number;
  educationFormId?: number;
  paymentFormId?: number;
  admissionBasis?: AdmissionBasis;
  showClosed?: boolean;
};

enum TargetAgreeStatus {
  REFUSED_BY_EMPLOYER = 'REFUSED_BY_EMPLOYER', // Заявка отклонена Заказчиком
  REFUSED_DUE_TO_EXPIRATION = 'REFUSED_DUE_TO_EXPIRATION', // Заявка отклонена в связи с истечением срока рассмотрения
  ACCEPTED_BY_EMPLOYER = 'ACCEPTED_BY_EMPLOYER', // Заявка одобрена Заказчиком
  UNDER_CONSIDERATION_BY_EMPLOYER = 'UNDER_CONSIDERATION_BY_EMPLOYER', // На рассмотрении у заказчика
  REVOKED_BY_APPLICANT = 'REVOKED_BY_APPLICANT', // Заявка отозвана гражданином
}

enum CompetitiveGroupStatus {
  NEW = 'NEW', // Новое
  RECEIVED_BY_UNIVERSITY = 'RECEIVED_BY_UNIVERSITY', // Получено вузом
  ADDITIONAL_INFORMATION_REQUIRED = 'ADDITIONAL_INFORMATION_REQUIRED', // Запрос дополнительных сведений
  UNDER_CONSIDERATION = 'UNDER_CONSIDERATION', // На рассмотрении
  PROFILE_EDITING = 'PROFILE_EDITING', // Редактирование профиля
  EXAM_DATE_ASSIGNED = 'EXAM_DATE_ASSIGNED', // Назначена дата ВИ
  EXAM_DATE_SELECTION = 'EXAM_DATE_SELECTION', // Выбор даты ВИ
  ON_COMPETITION = 'ON_COMPETITION', // Участвует в конкурсе
  AGREEMENT = 'AGREEMENT', // Согласие
  COMPETITION_FAILED = 'COMPETITION_FAILED', // Не прошло по конкурсу
  INCLUDED_IN_ENROLLMENT_ORDER = 'INCLUDED_IN_ENROLLMENT_ORDER', // Включено в приказ на зачисление
  REFUSED = 'REFUSED', // Отклонено
  AGREEMENT_REVOKED = 'AGREEMENT_REVOKED', // Отзыв согласия
  ENROLLMENT_REFUSED = 'ENROLLMENT_REFUSED', // Отказ от зачисления
  REVOKED_BY_APPLICANT = 'REVOKED_BY_APPLICANT', // Отозвано // Отозвано
  AGREEMENT_EDUCATION_DOCUMENT = 'AGREEMENT_EDUCATION_DOCUMENT', // Согласие/Документ об образовании
  AGREEMENT_EDUCATION_DOCUMENT_REVOKED = 'AGREEMENT_EDUCATION_DOCUMENT_REVOKED', // Отзыв согласие/Документ об образовании
  ENROLLED = 'ENROLLED', // Зачислен
  WAITING_EXAM = 'WAITING_EXAM', // Ожидание результатов ВИ
}

export type ChosenDirectionWithPriority = {
  id: number;
  applicationId: number;
  priority: number;
  agreement?: boolean;
  direction: Direction;
  createdAt?: DateString;
  deletedAt?: DateString;
  refuseEnrollmentDate?: DateString;
  profileSubjectScore?: number | null;
  rtTargetAgreeStatus?: TargetAgreeStatus | null;
  rtTargetAgreeCreatedAt?: DateString | null;
  selectedPrivilege?: Privilege;
  admitted?: boolean;
  entranceTestsSatisfied?: boolean;
  privilegeAdmitted?: boolean;
  targetContractPresent?: boolean;
  epguStatus?: CompetitiveGroupStatus;
};

export enum AdmissionBasis {
  BUDGET = 'BUDGET', // Бюджетная основа
  CONTRACT = 'CONTRACT', // Контракт
  FOREIGN = 'FOREIGN', // Как иностранный гражданин
  SPECIAL_QUOTA = 'SPECIAL_QUOTA', // Особая квота
  EXTRA_QUOTA = 'EXTRA_QUOTA', // Отдельная квота
  QUOTA = 'QUOTA', // По направлению Минобрнауки
  GOVERNMENT = 'GOVERNMENT', // Целевой прием
}

export const admissionBasesToLabels: { [key in AdmissionBasis]: string } = {
  [AdmissionBasis.BUDGET]: 'Бюджетная основа',
  [AdmissionBasis.CONTRACT]: 'Контракт',
  [AdmissionBasis.FOREIGN]: 'Как иностранный гражданин',
  [AdmissionBasis.SPECIAL_QUOTA]: 'Особая квота',
  [AdmissionBasis.EXTRA_QUOTA]: 'Отдельная квота',
  [AdmissionBasis.QUOTA]: 'По направлению Минобрнауки',
  [AdmissionBasis.GOVERNMENT]: 'Целевой прием',
};
